export const PLATFORM_BOOKING_FEE_GHS = 5;

/** Buukd platform revenue only — not shown in merchant billing, revenue totals, or business payouts. */
export const BUUKD_PLATFORM_ONLY_PAYMENT_CONTEXTS = [
  "appointment_reservation",
  "appointment_group_reservation",
  "platform_signup_fee",
  "platform_subscription_setup",
] as const;

export function isBuukdPlatformOnlyPaymentContext(context: string | null | undefined): boolean {
  if (!context) return false;
  return (BUUKD_PLATFORM_ONLY_PAYMENT_CONTEXTS as readonly string[]).includes(context);
}

export type PlatformPaymentSettlement = {
  id: string;
  business_id: string;
  currency: string;
  gross_amount: number;
  fee_amount: number;
  net_amount: number;
  business_fee_amount?: number | null;
  customer_fee_amount?: number | null;
  total_charged_amount?: number | null;
  platform_booking_fee_amount?: number | null;
  platform_revenue_amount?: number | null;
  fee_model?: string | null;
  paid_at: string | null;
  payout_eligible_at?: string | null;
  payer_email: string;
  context: string;
  provider_reference?: string | null;
  payout_status?: string;
  context_id?: string | null;
  /** platform = Buukd collects & settles; business = merchant's own Paystack */
  provider_account?: string | null;
};

export type PayoutBatch = {
  id: string;
  business_id: string;
  currency: string;
  total_gross_amount: number;
  total_fee_amount: number;
  total_net_amount: number;
  payout_reference: string | null;
  payout_notes: string | null;
  payout_channel: string | null;
  processed_at: string;
  created_at: string;
  business?: { id: string; name: string } | null;
  item_count?: number;
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  GHS: "₵",
  NGN: "₦",
  KES: "KSh",
};

export function formatSettlementCurrency(amount: number, currency = "GHS"): string {
  const sym = CURRENCY_SYMBOLS[currency] || currency;
  return `${sym}${Number(amount || 0).toFixed(2)}`;
}

export function paymentContextLabel(context: string): string {
  const labels: Record<string, string> = {
    appointment_deposit: "Booking deposit",
    appointment_group_deposit: "Group booking deposit",
    appointment_reservation: "Reservation fee",
    appointment_group_reservation: "Reservation fee",
    appointment_balance: "Booking balance",
    invoice_payment: "Invoice",
    day_pass_payment: "Day pass",
    membership_payment: "Membership",
    platform_subscription_setup: "Signup / subscription",
    platform_signup_fee: "Activation fee",
    event_registration: "Event",
  };
  return labels[context] || context.replace(/_/g, " ");
}

export function platformFeeForPayment(p: PlatformPaymentSettlement): number {
  if (p.platform_booking_fee_amount != null) {
    return Number(p.platform_booking_fee_amount);
  }
  if (p.platform_revenue_amount != null) {
    return Number(p.platform_revenue_amount);
  }
  return Number(p.fee_amount || 0);
}

export function customerPaidAmount(p: PlatformPaymentSettlement): number {
  if (p.total_charged_amount != null) {
    return Number(p.total_charged_amount);
  }
  return Number(p.gross_amount || 0) + Number(p.customer_fee_amount || 0);
}

/** Customer Paystack charge: service amount + flat platform fee (paid by the booker, all billing models). */
export function customerPaystackChargeAmount(
  grossAmount: number,
  _billingModel?: string | null,
): number {
  const gross = Number(grossAmount || 0);
  return Number((gross + PLATFORM_BOOKING_FEE_GHS).toFixed(2));
}

export function customerPlatformBookingFeeAmount(_billingModel?: string | null): number {
  return PLATFORM_BOOKING_FEE_GHS;
}

export function isBuukdPlatformSettlementPayment(p: PlatformPaymentSettlement): boolean {
  const acct = String(p.provider_account || "platform").toLowerCase();
  return acct === "platform";
}

export function isPaymentReadyForPayout(p: PlatformPaymentSettlement, now = new Date()): boolean {
  if (!p.paid_at) return false;
  if (p.payout_status && p.payout_status !== "eligible") return false;
  if (!p.payout_eligible_at) return true;
  return new Date(p.payout_eligible_at).getTime() <= now.getTime();
}

export function groupPaymentsByBusiness<T extends PlatformPaymentSettlement>(
  payments: T[],
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const p of payments) {
    const key = String(p.business_id);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(p);
  }
  return map;
}

export function sumPaymentBatch(payments: PlatformPaymentSettlement[]) {
  const currency = payments[0]?.currency || "GHS";
  let gross = 0;
  let platformFee = 0;
  let customerPaid = 0;
  let net = 0;
  for (const p of payments) {
    gross += Number(p.gross_amount || 0);
    platformFee += platformFeeForPayment(p);
    customerPaid += customerPaidAmount(p);
    net += Number(p.net_amount || 0);
  }
  return { currency, gross, platformFee, customerPaid, net, count: payments.length };
}

export type BusinessPayoutDestination = {
  payment_method?: string | null;
  payment_bank?: string | null;
  payment_account_number?: string | null;
  payment_mobile_money_network?: string | null;
  payment_mobile_money_number?: string | null;
  payment_connected?: boolean | null;
};

const PAYOUT_METHOD_LABELS: Record<string, string> = {
  bank: "Bank transfer",
  momo: "Mobile Money",
  mpesa: "M-Pesa",
};

function humanizePayoutToken(value: string | null | undefined): string {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** Super-admin display of where to send a business settlement. */
export function getBusinessPayoutDestination(business?: BusinessPayoutDestination | null): {
  connected: boolean;
  method: string | null;
  methodLabel: string;
  providerLabel: string | null;
  accountDetail: string | null;
  summary: string;
} {
  const method = String(business?.payment_method || "").trim() || null;
  const methodLabel = method ? (PAYOUT_METHOD_LABELS[method] || humanizePayoutToken(method)) : "Not set";

  if (method === "bank" && business?.payment_bank && business?.payment_account_number) {
    const providerLabel = humanizePayoutToken(business.payment_bank);
    const accountDetail = String(business.payment_account_number).trim();
    return {
      connected: business?.payment_connected !== false,
      method,
      methodLabel,
      providerLabel,
      accountDetail,
      summary: `${methodLabel} · ${providerLabel} · ${accountDetail}`,
    };
  }

  if (
    (method === "momo" || method === "mpesa")
    && business?.payment_mobile_money_number
  ) {
    const providerLabel = humanizePayoutToken(
      business.payment_mobile_money_network || (method === "mpesa" ? "M-Pesa" : "Mobile Money"),
    );
    const accountDetail = String(business.payment_mobile_money_number).trim();
    return {
      connected: business?.payment_connected !== false,
      method,
      methodLabel,
      providerLabel,
      accountDetail,
      summary: `${methodLabel} · ${providerLabel} · ${accountDetail}`,
    };
  }

  return {
    connected: false,
    method,
    methodLabel: method ? methodLabel : "Not connected",
    providerLabel: null,
    accountDetail: null,
    summary: "No MoMo or bank payout details on file",
  };
}

